import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { ILearnActionService } from '@intigral/domain/lib/application/services/LearnActionService';
import { IMpxService } from '@intigral/domain/lib/application/services/MpxService';
import { useMediaQuery } from '@intigral/hooks';
import { Box, Button, FavSurvey, FlexWrapper, LinkWrapper, Separator, Skeleton, Text } from '@intigral/shared';
import withResponsiveStyles from '@intigral/shared/components/hocs/ResponiseStyles/withResponsiveStyles';
import withPresetWidth from '@intigral/shared/lib/components/hocs/withPresetWidth/withPresetWidth';
import { convertHttpToHttps } from '@intigral/utils';
import { get, set } from '@intigral/utils/lib/browser-storage/localStorage';
import Link from 'next/link';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { STORE_NAME } from '../../config/constants';
import { EVENT, KEYS } from '../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import h, { getCountry, getGuid } from '../../helpers/common';
import user from '../../helpers/user';
import { __ } from '../../translations';
import PasswordPopup from '../elements/PasswordPopup';
import url from '../../helpers/url';
import { useQuery } from '@tanstack/react-query';
import type { IProfileData } from '../../types/ILocalStorage';
import { CopyrightText, HeaderWcp } from '@intigral/ui';
import { Link as RouterLink } from 'react-router-dom';
import { selectProfileDone } from '../../store/reducers/profileSlice';
import { useConfiguration } from '../../hooks/useConfiguration';
import { urlAfterProfileSelect } from '../../utils/pendingDeepLink';

const FlexWidth = withPresetWidth(FlexWrapper);
const StyleButton = withResponsiveStyles(Button);

// We need this element to toggle the "overflow-x" CSS property, which affects the button's sticky behavior.
const rootElement = document.getElementById('root');
// This makes the sticky effect on the button works as expected.
const removeOverflowEffect = () => {
    rootElement.style.overflowX = 'unset';
};
// This will reset the "overflow-x" property to it's previous state.
const resetOverflowEffect = () => {
    rootElement.style.removeProperty('overflow-x');
};

type FeedItem = {
    'VOD Name': string;
    GUID: string;
    'VOD Thumbnail': string;
};

type FeedEntry = {
    id: string;
    title: string;
    guid: string;
    programType: string;
    ratings: {
        rating: string;
    }[];
    tags: {
        scheme: string;
        title: string;
    }[];
    thumbnails: {
        'portrait-highestResolution': {
            url: string;
        };
    };
    year: number;
};

type FeedResponse = {
    entries: FeedEntry[];
};

type IColdStartConfiguration = {
    coldStartEnable: boolean;
    minimumNumberOfAssetSelectionCount: number;
    assetSelectionFeed: string;
};

function transformResponseEntry(item: FeedEntry, config: any) {
    return {
        'VOD AssetID': item.id,
        'VOD Name': item.title,
        GUID: item.guid,
        'VOD Thumbnail': h.getImagizerUrl(
            convertHttpToHttps(item.thumbnails['portrait-highestResolution'].url),
            config?.imagizer.components_images?.VOD_ITEM
        ),
        'VOD year': item.year,
        'VOD Rating':
            item.ratings?.find((ratingList) => typeof ratingList.rating === 'string')?.rating.toUpperCase() || '',
        'VOD Type': item.programType,
        'VOD Genre': item.tags?.find((tagList) => tagList.scheme === 'Genre').title || '',
    };
}

async function fetchColdStartItems(coldStartSetting: IColdStartConfiguration, ratings: string, config: any) {
    if (!coldStartSetting.coldStartEnable) return [];

    const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
    const response: FeedResponse = await mpxService.getFeed(coldStartSetting.assetSelectionFeed, {
        byProgramType: 'Movie|Series',
        count: true,
        byRatings: ratings,
        byCustomValue: `{PC}{${getCountry()}},{subscriptionGuids}{${decodeURIComponent(getGuid())}}`,
    });

    if (response?.entries?.length > 0) return response.entries.map((item) => transformResponseEntry(item, config));

    return [];
}

function handleProtectedProfile(profile: IProfileData, password?: string) {
    const isProtected: boolean = profile.passwordProtected;
    let epochMs: string = undefined;
    let encPassword: string = undefined;
    let isPasswordRequired = isProtected;

    if (isProtected) {
        const storedPassword: string =
            profile?.profilePassword || (typeof password === 'string' ? password : undefined);
        if (storedPassword) {
            epochMs = Date.now().toString();
            encPassword = window.btoa(`${epochMs}:${storedPassword || password}`);
            isPasswordRequired = false;
        }
    }

    return {
        isProtected,
        epochMs,
        encPassword,
        isPasswordRequired,
    };
}

function sendPreferenceItems(activeIds: string[], epochMs: string, encPassword: string) {
    const { apiKey, username, identityToken, authToken } = h.getStore(STORE_NAME.USER) || {};

    const learnActionService = container.resolve<ILearnActionService>(DI.SERVICES.ILearnActionService);

    learnActionService
        .setPreferencesAction(
            apiKey,
            {
                userName: username,
                device: 'PC',
                itemGuids: activeIds,
                profileName: user.getProfileName(),
                country: getCountry(),
            },
            identityToken,
            authToken,
            epochMs,
            encPassword
        )
        .then((response) => {
            if (response?.data?.status === 'success') user.initiatedUserProfiles(true);
        });
}

export const ColdStart = (props: any) => {
    const { data: config } = useConfiguration();
    const [activeIds, setActiveIds] = useState<string[]>([]);
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const [showPasswordPopup, setShowPasswordPopup] = useState(false);
    const headerElement = useRef<HTMLElement>(null);
    const { track, setSuperProperties } = useContext(MixpanelContext);
    const dispatch = useDispatch();

    const navigate = useNavigate();

    const coldStartSetting: IColdStartConfiguration = config?.coldStart;
    const neededSelections = coldStartSetting.minimumNumberOfAssetSelectionCount - activeIds.length;
    const minimumNotPicked = activeIds.length < coldStartSetting.minimumNumberOfAssetSelectionCount;
    const B2B_localization = user.getUserSegment() === 'B2B' ? 'b2b_' : '';
    const currentProfile: IProfileData = user.getProfile();

    const ratings: string = currentProfile?.newParentalControl || currentProfile?.parentalControl;

    const {
        isLoading,
        isError,
        data: coldStartItems,
    } = useQuery({
        queryKey: ['cold-start-items', ratings],
        queryFn: async () => await fetchColdStartItems(coldStartSetting, ratings, config),
        enabled: !!config && !!coldStartSetting,
    });
    const copyrights =
        __('home_footer_copyright') +
        ' ' +
        new Date().getFullYear() +
        ' <a href=' +
        __('home_footer_intigral_link') +
        'target=_blank>' +
        __('home_footer_intigral') +
        '</a>' +
        __('home_footer_all_right');

    const isMobile = useMediaQuery('(max-width: 924px)');

    const navigateToHomeScreen = () => {
        navigate(urlAfterProfileSelect(url.route('')));
        resetOverflowEffect();
    };

    const handleClick = (item: FeedItem) => () => {
        setActiveIds((prev) => {
            if (prev.includes(item.GUID)) {
                return prev.filter((currentId) => currentId !== item.GUID);
            } else {
                track(EVENT.COLD_START.COLD_START_SELECT, item);
                return [...prev, item.GUID];
            }
        });
    };

    const submitHandler = (ev: React.MouseEvent<any, MouseEvent>, password?: string) => {
        if (typeof ev !== 'undefined') ev.preventDefault();

        const { isProtected, isPasswordRequired, epochMs, encPassword } = handleProtectedProfile(
            currentProfile,
            password
        );

        if (isProtected) {
            setShowPasswordPopup(isPasswordRequired);
            if (isPasswordRequired) return;
        }

        setShowSpinner(true);

        if (!minimumNotPicked)
            track(EVENT.COLD_START.COLD_START_SUBMIT, {
                'VOD List': coldStartItems
                    .filter((item) => activeIds.includes(item.GUID))
                    .map((item) => item['VOD Name']),
            });

        sendPreferenceItems(minimumNotPicked ? [] : activeIds, epochMs, encPassword);

        // Set showWelcomeScreen to true to trigger gamification modal after cold-start
        const selectedProfile = get(STORE_NAME.SELECTED_PROFILE);
        if (selectedProfile) {
            // timeout to avoid race condition
            setTimeout(() => {
                const updatedProfile = {
                    ...selectedProfile,
                    showWelcomeScreen: true,
                };
                set(STORE_NAME.SELECTED_PROFILE, updatedProfile);
                // Update Redux store as well
                dispatch(selectProfileDone(updatedProfile));
            }, 2000);
        }

        navigateToHomeScreen();

        return false;
    };

    useEffect(() => {
        removeOverflowEffect();
        if (coldStartSetting.coldStartEnable) {
            setSuperProperties({ [KEYS.SUPER.PREVIOUS_SCREEN]: EVENT.PROFILE.PROFILES_VIEW });
            track(EVENT.COLD_START.COLD_START_VIEW, {});
        } else {
            navigateToHomeScreen();
        }
    }, []);

    if (isError) navigateToHomeScreen();

    return (
        <>
            <HeaderWcp
                logoProps={{
                    homeUrl: '/',
                    title: 'STC logo',
                    logoSrc: 'images/big-logo.svg',
                    LinkComponent: 'a',
                }}
                staticChildren={
                    <LinkWrapper
                        link={{
                            linkComponent: RouterLink,
                            href: '#',
                            text: __('parental_pin_management_form_skip'),
                            transformLinkProps: () => ({
                                onClick: (e) => {
                                    e.preventDefault();
                                    submitHandler(e, undefined);
                                },
                            }),
                        }}
                        data-test="cold-start"
                        className="primary-brand"
                    />
                }
            />
            <div
                style={{ paddingTop: (headerElement.current ? headerElement.current.offsetHeight : 0) + 'px' }}
                className="container"
            >
                <FlexWidth presetWidth="large" gap="2x" direction="column">
                    {!isLoading && (
                        <>
                            <FlexWrapper gap="2x" direction="column">
                                <Text margin="0" alignment="start" as="h2" variant="h3" data-test-id="cold-start-title">
                                    {__('cold_start_title').replace(
                                        '{minimum}',
                                        coldStartSetting.minimumNumberOfAssetSelectionCount
                                    )}
                                </Text>
                                <Text margin="0" alignment="start" as="p" color="gray" data-test-id="cold-start-desc">
                                    {__('cold_start_desc')}
                                </Text>
                            </FlexWrapper>
                            <FlexWrapper itemCount={{ default: 3, md: 4 }} gap="2x" data-test-id="cold-start-items">
                                {coldStartItems.map((item) => (
                                    <FavSurvey
                                        id={item.GUID}
                                        key={item.GUID}
                                        selected={activeIds.includes(item.GUID)}
                                        onClick={handleClick(item)}
                                        image={item['VOD Thumbnail']}
                                    />
                                ))}
                            </FlexWrapper>
                            <Box
                                theme="bg"
                                padding={{ default: '1x', md: '3x' }}
                                style={{
                                    position: 'sticky',
                                    bottom: '0',
                                    zIndex: '10',
                                }}
                                data-test-id="cold-start-submit-button"
                            >
                                <StyleButton
                                    disabled={minimumNotPicked}
                                    LinkComponent={Link}
                                    href="#"
                                    onClick={(ev) => !minimumNotPicked && submitHandler(ev, undefined)}
                                    loading={showSpinner}
                                    placement={isMobile ? {} : 'center'}
                                    data-test-id="cold-start-submit-button-text"
                                >
                                    {minimumNotPicked
                                        ? __('cold_start_btn_pick').replace('{remaining}', neededSelections)
                                        : __('cold_start_letsgo')}
                                </StyleButton>
                            </Box>
                        </>
                    )}
                    {isLoading && (
                        <>
                            <FlexWrapper
                                gap="2x"
                                direction="column"
                                paddingTop="4x"
                                data-test-id="cold-start-loading-skeleton"
                            >
                                <Skeleton variant="text" width="45%" height="2rem" />
                                <Skeleton variant="text" />
                                <Skeleton variant="text" width="68%" />
                            </FlexWrapper>
                            <FlexWrapper itemCount={{ default: 3, md: 4 }} gap="2x">
                                {Array.from({ length: 9 }, (_, index) => (
                                    <FavSurvey key={index} image="" id="" isLoading />
                                ))}
                            </FlexWrapper>
                        </>
                    )}
                </FlexWidth>
                <PasswordPopup
                    data-test-id="cold-start-password-popup"
                    showPopup={showPasswordPopup}
                    onSuccess={(password: string) => submitHandler(undefined, password)}
                    canClose={true}
                    title={__(B2B_localization + 'parental_pin_management_password_title')}
                    description={__(B2B_localization + 'cold_start_password_desc')}
                    cancelButtonText={__('parental_pin_management_password_cancel_btn')}
                    buttonText={__('parental_pin_management_password_submit_btn')}
                />
            </div>
            {/* <div
                data-test-id="cold-start-footer"
                className="copyright-main"
                style={{ borderTop: '1px solid #3d4852', marginTop: '64px', padding: '40px 0px' }}
            >
                <p className="footer-copyright">
                    {__('home_footer_copyright')} {new Date().getFullYear()}{' '}
                    {/* We use achor tag here because the link will be opened in a new tab */}
            {/* <a
                        data-test-id="cold-start-footer-intigral-link"
                        href={__('home_footer_intigral_link')}
                        target="_blank"
                    >
                        {__('home_footer_intigral')}
                    </a>{' '}
                    {__('home_footer_all_right')}
                </p>
            </div>  */}
            <Separator theme="none" margin="1x" style={{ maxWidth: '90vw' }} />
            <CopyrightText text={copyrights} />
        </>
    );
};
